/**
 * Built-in courses for Tavi's Workshop.
 *
 * Level data is authored programmatically with helpers from `paint.ts`.
 * What leaves this module is plain, serialisable `LevelData`.
 *
 * Play order starts with the five classic courses, then the expanded workshop
 * catalogue (see `workshop.ts`). Gaps are usually ≤ 4 tiles; crates float 2–4
 * rows above a surface; goal/castle share a tx via `finish`.
 */

import { Terrain, type LevelData, type ObjectPlacement } from '../game/types'
import {
  blank,
  box,
  catwalk,
  coinArc,
  coins,
  carve,
  finish,
  floor,
  ledge,
  place,
  placeRow,
  pipe,
  scenery,
  stair,
  strip,
  W,
  H,
} from './paint'
import { WORKSHOP_COURSES, WORKSHOP_LIST } from './workshop'

// ── 1. Green Hills ─────────────────────────────────────────────────────────

/**
 * Design note — the gentle one. Fourteen tiles of nothing at all so the player
 * finds their feet, then one Rivet on a long flat, then the ? row, then the
 * pipes, then the first real gap. The hard-block staircase near the end is the
 * only thing that asks for anything, and it hands over a Spare Battery for the trouble.
 */
function greenHills(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  // Rolling floor with three gaps: three tiles, then four, then four.
  floor(g, 0, 40, 25)
  floor(g, 44, 66, 25)
  floor(g, 71, 96, 25)
  floor(g, 101, 159, 25)

  // A one-tile step, and the brick shelf the second pipe leads up to.
  box(g, 45, 24, 45, 24, Terrain.HARD)
  strip(g, 50, 22, 57, Terrain.BRICK)
  box(g, 51, 23, 51, 23, Terrain.HARD)
  // The staircase: ten one-tile risers, nine tiles of altitude, a prize on top.
  stair(g, 110, 24, 10, 1, 24)

  scenery(o, [
    [6, 9, 'cloud'],
    [24, 7, 'cloud2'],
    [50, 9, 'cloud'],
    [78, 8, 'cloud2'],
    [120, 8, 'cloud'],
    [13, 23, 'hill'],
    [34, 24, 'bush'],
    [66, 23, 'hill'],
    [120, 24, 'bush'],
  ])
  placeRow(o, 'fence', 130, 24, 133)

  // A single Rivet to teach stomping, safely early.
  place(o, 'rivet', 13, 24)

  // The ? row. Head height from the floor is four tiles, so all four are up.
  place(o, 'crate', 17, 21)
  place(o, 'brick', 19, 21)
  place(o, 'crate', 21, 21)
  place(o, 'crate-expansion-cell', 23, 21)

  // Two pipes, one step apart, so the lip is always approachable. Lips at 23.
  pipe(o, 30, 23, 3)
  coins(o, 30, 31, 21)
  pipe(o, 48, 23, 3)
  coins(o, 48, 49, 20)

  // Across the first gap: a Rivet, then coins tracing the arc of the jump.
  place(o, 'rivet', 56, 24)
  coinArc(o, 41, 43, 24)
  coinArc(o, 66, 70, 24)

  // The shelf itself: an Ember Cell, a hidden Spare Battery and a line of coins.
  place(o, 'crate-ember-cell', 53, 20)
  place(o, 'crate', 51, 20, { contains: 'spare-battery' })
  coins(o, 55, 57, 21)

  // The high shelf over the third gap, and a ? row over the solid run after it.
  box(g, 88, 22, 88, 22, Terrain.HARD)
  place(o, 'rivet', 84, 24)
  place(o, 'rivet', 91, 24)
  place(o, 'scout', 94, 24)
  coins(o, 88, 88, 21)
  place(o, 'brick', 102, 21)
  place(o, 'crate', 103, 21)
  place(o, 'crate', 104, 21)
  coinArc(o, 96, 100, 24)

  // The run home.
  place(o, 'rivet', 106, 24)
  place(o, 'rivet', 124, 24)
  place(o, 'rivet', 127, 24)
  place(o, 'scout', 133, 24)
  place(o, 'expansion-cell', 76, 24)
  place(o, 'hidden-cache', 144, 21, { contains: 'spare-battery' })
  coins(o, 137, 141, 24)

  // The prize at the top of the staircase.
  coins(o, 118, 119, 14)
  place(o, 'spare-battery', 119, 13)

  finish(o, 150, 25)

  return {
    name: 'Green Hills',
    author: 'the workshop',
    width: W,
    height: H,
    theme: 'overworld',
    time: 400,
    startX: 3,
    startY: 24,
    tiles: g,
    objects: o,
  }
}

// ── 2. Pipe Dreams ─────────────────────────────────────────────────────────

/**
 * Design note — the vertical course, built on one idea: pipes are stairs. You
 * learn the ladder on flat ground, walk a Scout into a brick alley, cross a
 * fifteen-tile pit on a moving platform and two one-way stones, then the ground
 * turns into a spring that throws you up a chain of ledges to a long catwalk.
 * The flag is a vertical lift away, so the whole level is one long climb.
 */
function pipeDreams(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  // Two floor runs with a fifteen-tile lethal pit between them.
  floor(g, 0, 40, 25)
  floor(g, 56, 159, 25)

  // The pipe ladder, lips at rows 23, 22 and 21.
  pipe(o, 6, 23, 3)
  pipe(o, 11, 22, 4)
  pipe(o, 15, 21, 5)
  coins(o, 6, 7, 21)
  coins(o, 11, 12, 20)
  coins(o, 15, 16, 19)
  place(o, 'crate-expansion-cell', 19, 19)
  place(o, 'brick', 25, 24)
  place(o, 'brick', 26, 24)
  place(o, 'rivet', 28, 24)
  placeRow(o, 'fence', 2, 24, 4)
  scenery(o, [
    [9, 8, 'cloud'],
    [32, 7, 'cloud2'],
    [60, 9, 'cloud'],
    [96, 8, 'cloud2'],
    [120, 7, 'cloud'],
    [70, 23, 'hill'],
    [104, 24, 'bush'],
  ])

  // The shell alley: stomp the Scout, kick the shell, break the bricks.
  place(o, 'scout', 35, 24)
  place(o, 'brick', 36, 24)
  place(o, 'brick', 37, 24)
  place(o, 'prism', 39, 24)
  // A floating ? row over the same stretch, three tiles up.
  place(o, 'brick', 31, 21)
  place(o, 'crate', 32, 21)
  place(o, 'brick', 33, 21)

  // The pit: empty to the bottom of the course, bridged by a moving platform
  // and two one-way stones.
  place(o, 'moving-platform-h', 43, 23, { range: 3, speed: 0.5 })
  placeRow(o, 'platform', 48, 23, 50)
  placeRow(o, 'platform', 53, 23, 55)
  coinArc(o, 41, 55, 25)

  // The exit terrace, then the checkpoint on the floor below it.
  ledge(g, 56, 22, 62, 24, Terrain.HARD)
  place(o, 'scout', 59, 21)
  place(o, 'rivet', 61, 21)
  place(o, 'checkpoint', 64, 24)

  // The spring, and the chain of one-way ledges it hands you to.
  place(o, 'spring', 70, 24)
  placeRow(o, 'platform', 72, 20, 80)
  placeRow(o, 'platform', 82, 18, 86)
  placeRow(o, 'platform', 88, 16, 92)
  placeRow(o, 'platform', 95, 14, 99)
  coins(o, 74, 79, 19)
  place(o, 'spare-battery', 84, 17)

  // The catwalk, thirty tiles of it, and a second checkpoint near the far end.
  ledge(g, 100, 14, 132, 14, Terrain.HARD)
  place(o, 'crate', 104, 12)
  place(o, 'brick', 105, 12)
  place(o, 'rivet', 107, 13)
  coins(o, 108, 111, 13)
  place(o, 'scout', 114, 13)
  place(o, 'rivet', 118, 13)
  place(o, 'checkpoint', 122, 13)
  place(o, 'hidden-cache', 128, 12, { contains: 'expansion-cell' })
  coins(o, 125, 130, 13)

  // The lift is the only way onto the last tier.
  place(o, 'moving-platform', 135, 24, { range: 10, speed: 0.35 })
  placeRow(o, 'platform', 137, 14, 141)
  ledge(g, 143, 12, 159, 24, Terrain.GROUND)
  place(o, 'rivet', 148, 11)
  place(o, 'scout', 152, 11)
  place(o, 'crate-prism', 155, 11)
  coins(o, 149, 153, 11)
  finish(o, 150, 12)

  return {
    name: 'Pipe Dreams',
    author: 'the workshop',
    width: W,
    height: H,
    theme: 'overworld',
    time: 350,
    startX: 3,
    startY: 24,
    tiles: g,
    objects: o,
  }
}

// ── 3. Cavern Depths ───────────────────────────────────────────────────────

/**
 * Design note — the climbing course, and the first where the floor is a
 * suggestion. Ceilings at head height, shafts you drop down, a single-tile
 * crawl to an Ember Cell, Wisps that come straight through rock, spikes under
 * the lava sump and lava under everything else. The spine is an eleven-ledge
 * zig-zag up a chimney; coins sit on the safe side of every ledge so the
 * greedy line is always the dangerous one.
 */
function cavernDepths(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  // Bedrock ceiling, then the five floor runs.
  box(g, 0, 0, 159, 3, Terrain.GROUND)
  floor(g, 0, 18, 25)
  floor(g, 22, 32, 25)
  floor(g, 40, 56, 25)
  floor(g, 58, 72, 25)
  floor(g, 76, 159, 25)

  // The holes: a lava sump, a chimney, a skylight and the Wisp corridor.
  carve(g, 19, 25, 39, 27)
  carve(g, 42, 4, 49, 24)
  carve(g, 44, 0, 56, 3)
  carve(g, 58, 18, 72, 24)
  carve(g, 90, 25, 120, 27)

  // Painted after the carves: the step into the sump, the step into the
  // chimney, and the shelf in the entry corridor.
  box(g, 18, 24, 18, 24, Terrain.HARD)
  box(g, 50, 24, 51, 24, Terrain.HARD)
  ledge(g, 10, 23, 15, 24, Terrain.HARD)

  // The sump: spikes on the left, lava under the rest, one-way stones across.
  box(g, 19, 28, 39, 29, Terrain.GROUND)
  placeRow(o, 'spike', 21, 24, 25)
  placeRow(o, 'lava', 19, 27, 39)
  catwalk(g, 22, 23, 23)
  catwalk(g, 26, 22, 27)
  catwalk(g, 30, 23, 31)
  catwalk(g, 34, 22, 35)
  catwalk(g, 38, 23, 39)
  coins(o, 22, 23, 22)
  coins(o, 30, 31, 22)
  coins(o, 34, 35, 21)
  place(o, 'crate', 34, 20, { contains: 'expansion-cell' })

  // The chimney: eleven one-way ledges, alternating sides, two rows apart. The
  // top two sit under the skylight, so the climb has a way out at the top.
  const chimney: Array<[number, number, number]> = [
    [42, 22, 47],
    [44, 20, 49],
    [42, 18, 47],
    [44, 16, 49],
    [42, 14, 47],
    [44, 12, 49],
    [42, 10, 47],
    [44, 8, 49],
    [42, 6, 47],
    [44, 4, 49],
    [44, 2, 49],
  ]
  for (const [x0, ty, x1] of chimney) {
    catwalk(g, x0, ty, x1)
    coins(o, x0 + 1, x1 - 1, ty - 1)
  }
  place(o, 'spare-battery', 48, 12)
  coins(o, 48, 48, 20)
  coins(o, 48, 48, 6)
  place(o, 'wisp', 48, 18)
  place(o, 'wisp', 46, 8)

  // The Wisp corridor, mined out of the rock and patrolled by a saw.
  place(o, 'rivet', 61, 24)
  place(o, 'scout', 66, 24)
  place(o, 'brick', 68, 21)
  place(o, 'crate', 61, 21)
  place(o, 'hidden-cache', 70, 21, { contains: 'expansion-cell' })
  place(o, 'saw', 64, 19, { range: 3, speed: 0.35 })
  coins(o, 63, 64, 24)

  // The flume: a checkpoint, a Spare Battery, then thirty tiles of lava.
  place(o, 'checkpoint', 78, 24)
  place(o, 'spare-battery', 80, 24)
  place(o, 'crate-ember-cell', 82, 21)
  coins(o, 84, 88, 24)
  placeRow(o, 'lava', 90, 27, 120)
  catwalk(g, 91, 23, 93)
  catwalk(g, 97, 23, 99)
  placeRow(o, 'platform', 102, 23, 103)
  catwalk(g, 106, 23, 108)
  catwalk(g, 112, 23, 114)
  catwalk(g, 118, 23, 120)
  coins(o, 91, 93, 22)
  coins(o, 106, 108, 22)
  coins(o, 112, 114, 22)
  place(o, 'prism', 97, 22)
  place(o, 'shardling', 118, 22)

  // The run home.
  place(o, 'rivet', 132, 24)
  place(o, 'wanderer', 134, 24)
  place(o, 'rivet', 138, 24)
  place(o, 'checkpoint', 143, 24)
  place(o, 'spring', 124, 24)
  place(o, 'note-block', 127, 24)
  coins(o, 146, 149, 24)
  finish(o, 150, 25)

  return {
    name: 'Cavern Depths',
    author: 'the workshop',
    width: W,
    height: H,
    theme: 'underground',
    time: 400,
    startX: 3,
    startY: 24,
    tiles: g,
    objects: o,
  }
}

// ── 4. Spike Gauntlet ──────────────────────────────────────────────────────

/**
 * Design note — the kaizo course, and the only one where the lava is the
 * floor. A single white tile of HARD is a safe step and everything either side
 * of it is death, and the whole thing climbs so that a mistake costs you height
 * as well as a life. The ice run is the one place the rules change underneath
 * you, a Shardling can only be jumped, never stomped, and three checkpoints sit
 * at the head of each act.
 */
function spikeGauntlet(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  // A stone bed under a lava lake: falling is always fatal.
  floor(g, 0, 159, 28, Terrain.HARD)
  placeRow(o, 'lava', 2, 27, 157)

  // Act I — the spike run. The entry bank is the only solid ground for miles.
  box(g, 0, 24, 8, 26, Terrain.GROUND)
  placeRow(o, 'spike', 9, 26, 15)
  ledge(g, 16, 23, 19, 26, Terrain.HARD)
  placeRow(o, 'spike', 20, 26, 24)
  ledge(g, 25, 22, 27, 26, Terrain.HARD)
  placeRow(o, 'spike', 28, 26, 29)
  ledge(g, 30, 21, 33, 26, Terrain.HARD)
  place(o, 'rivet', 32, 20)
  place(o, 'checkpoint', 33, 20)
  ledge(g, 35, 20, 38, 26, Terrain.HARD)
  place(o, 'spike', 37, 19)
  placeRow(o, 'ice-block', 40, 26, 49)
  placeRow(o, 'spike', 50, 26, 52)
  ledge(g, 53, 24, 59, 26, Terrain.HARD)
  place(o, 'crate', 55, 22)
  place(o, 'crate-expansion-cell', 58, 22)
  place(o, 'rivet', 58, 23)

  // Act II — the saw hall.
  ledge(g, 61, 23, 67, 26, Terrain.HARD)
  place(o, 'spike', 64, 22)
  place(o, 'saw', 70, 21, { range: 3, speed: 0.4 })
  ledge(g, 73, 23, 76, 26, Terrain.HARD)
  place(o, 'saw-h', 80, 24, { range: 5, speed: 0.4 })
  ledge(g, 85, 24, 87, 26, Terrain.HARD)
  place(o, 'saw', 91, 22, { range: 3, speed: 0.45 })
  ledge(g, 94, 23, 97, 26, Terrain.HARD)
  place(o, 'checkpoint', 96, 22)
  place(o, 'saw-h', 102, 24, { range: 4, speed: 0.35 })
  ledge(g, 106, 23, 109, 26, Terrain.HARD)
  place(o, 'scout', 108, 22)
  ledge(g, 110, 24, 112, 26, Terrain.HARD)
  placeRow(o, 'spike', 113, 26, 118)
  ledge(g, 119, 25, 122, 26, Terrain.HARD)
  placeRow(o, 'spike', 123, 26, 127)
  ledge(g, 128, 24, 130, 26, Terrain.HARD)
  place(o, 'cannon', 129, 23)

  // Act III — the white block climb. Shardlings cannot be stomped, so jump them.
  ledge(g, 134, 23, 137, 26, Terrain.HARD)
  place(o, 'shardling', 136, 22)
  ledge(g, 138, 20, 141, 26, Terrain.HARD)
  place(o, 'shardling', 140, 19)
  ledge(g, 142, 18, 145, 26, Terrain.HARD)
  place(o, 'shardling', 144, 17)
  place(o, 'checkpoint', 145, 17)
  ledge(g, 146, 16, 148, 26, Terrain.HARD)
  place(o, 'shardling', 147, 15)
  // The last drop: a two-tile step, then the finish shelf behind two spikes.
  ledge(g, 150, 14, 151, 26, Terrain.HARD)
  box(g, 152, 12, 159, 26, Terrain.GROUND)
  placeRow(o, 'spike', 152, 11, 153)
  place(o, 'crate-prism', 157, 10)
  coins(o, 156, 156, 11)
  coins(o, 158, 158, 11)
  finish(o, 155, 12)

  return {
    name: 'Spike Gauntlet',
    author: 'the workshop',
    width: W,
    height: H,
    theme: 'castle',
    time: 300,
    startX: 3,
    startY: 23,
    tiles: g,
    objects: o,
  }
}

// ── 5. Star Ride ───────────────────────────────────────────────────────────

/**
 * Design note — the flow level. Two banks of solid ground, an island in the
 * middle, and everything else is one-way platform over open sky, so the whole
 * course reads as a single held direction: a running jump every four or five
 * tiles, most of them made at full speed. The Prism sits before the longest
 * chain, the middle platforms are exactly one body wide, and the vertical lift
 * is the only way back to the roof if you overshoot the lot in one go.
 */
function starRide(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  // Ground at the start, an island in the middle, ground at the finish.
  floor(g, 0, 24, 25)
  floor(g, 80, 84, 25)
  floor(g, 130, 159, 25)

  // The take-off ramp, then the step into nothing.
  stair(g, 18, 24, 3, 1, 24)
  place(o, 'rivet', 9, 24)
  place(o, 'rivet', 15, 24)
  place(o, 'bush', 4, 24)
  place(o, 'hill', 12, 23)
  placeRow(o, 'fence', 6, 24, 8)
  placeRow(o, 'cloud', 8, 11, 9)
  placeRow(o, 'cloud2', 20, 8, 21)
  place(o, 'cloud2', 34, 14)
  place(o, 'cloud', 50, 12)
  place(o, 'cloud', 62, 15)
  place(o, 'cloud2', 70, 13)
  place(o, 'cloud', 84, 12)
  place(o, 'cloud2', 100, 10)
  place(o, 'cloud', 112, 9)
  place(o, 'cloud2', 124, 12)

  // The first chain: four-tile platforms, four-tile gaps, all at running pace.
  placeRow(o, 'platform', 29, 23, 32)
  coins(o, 29, 31, 22)
  place(o, 'rivet', 31, 22)
  placeRow(o, 'platform', 36, 23, 39)
  coins(o, 36, 38, 22)
  placeRow(o, 'platform', 43, 22, 46)
  coins(o, 43, 45, 21)
  place(o, 'scout', 45, 21)
  placeRow(o, 'platform', 50, 22, 53)
  coins(o, 50, 52, 21)

  // The Prism, the spike that makes it tempting, and the long run home.
  placeRow(o, 'platform', 58, 22, 61)
  place(o, 'prism', 60, 20)
  coins(o, 58, 60, 21)
  placeRow(o, 'platform', 66, 22, 69)
  place(o, 'spike', 67, 21)
  place(o, 'wanderer', 69, 21)
  placeRow(o, 'platform', 74, 23, 77)
  place(o, 'rivet', 76, 22)

  // The island: two bodies wide between the spikes, and the lift off it.
  placeRow(o, 'spike', 80, 24, 80)
  placeRow(o, 'spike', 84, 24, 84)
  place(o, 'rivet', 83, 24)
  place(o, 'moving-platform', 82, 24, { range: 9, speed: 0.35 })

  // The roof, back at full speed.
  placeRow(o, 'platform', 85, 15, 89)
  placeRow(o, 'platform', 90, 14, 95)
  place(o, 'checkpoint', 87, 14)
  place(o, 'rivet', 93, 13)
  place(o, 'scout', 94, 13)
  placeRow(o, 'platform', 100, 14, 105)
  place(o, 'shardling', 103, 13)
  place(o, 'spare-battery', 104, 13)
  placeRow(o, 'platform', 110, 14, 115)
  place(o, 'rivet', 112, 13)
  place(o, 'checkpoint', 114, 13)
  placeRow(o, 'platform', 120, 14, 125)
  place(o, 'expansion-cell', 122, 13)
  placeRow(o, 'platform', 126, 14, 129)

  // The last landing, a two-tile pedestal to look at, and the run in.
  place(o, 'crate', 133, 21)
  place(o, 'brick', 134, 21)
  place(o, 'crate-expansion-cell', 135, 21)
  place(o, 'rivet', 138, 24)
  place(o, 'rivet', 141, 24)
  place(o, 'scout', 144, 24)
  coins(o, 137, 143, 24)
  ledge(g, 146, 23, 148, 24, Terrain.HARD)
  place(o, 'crate-ember-cell', 146, 21)
  place(o, 'note-block', 148, 21)
  coins(o, 146, 148, 20)
  finish(o, 150, 25)

  return {
    name: 'Star Ride',
    author: 'the workshop',
    width: W,
    height: H,
    theme: 'sky',
    time: 400,
    startX: 3,
    startY: 24,
    tiles: g,
    objects: o,
  }
}

// ── exports ────────────────────────────────────────────────────────────────

const CLASSIC: Record<string, LevelData> = {
  'Green Hills': greenHills(),
  'Pipe Dreams': pipeDreams(),
  'Cavern Depths': cavernDepths(),
  'Spike Gauntlet': spikeGauntlet(),
  'Star Ride': starRide(),
}

/** Every built-in course, keyed by name. */
export const COURSES: Record<string, LevelData> = {
  ...CLASSIC,
  ...WORKSHOP_COURSES,
}

/** The same courses in play order. */
export const COURSE_LIST: LevelData[] = [
  CLASSIC['Green Hills'] as LevelData,
  CLASSIC['Pipe Dreams'] as LevelData,
  CLASSIC['Cavern Depths'] as LevelData,
  CLASSIC['Spike Gauntlet'] as LevelData,
  CLASSIC['Star Ride'] as LevelData,
  ...WORKSHOP_LIST,
]
